import {
	Box,
	chakra,
	Flex,
	keyframes,
	usePrefersReducedMotion,
	useColorModeValue,
} from '@chakra-ui/react';
import { ReactNode } from 'react';

// Diagram geometry is drawn in a fixed 1400x570 coordinate space. The SVG scales
// with the container and the HTML labels are positioned in percent of it, with
// font sizes in cqw so text scales with the pipes.
const W = 1400;
const H = 570;
const px = (size: number) => `${(size * 100 / W).toFixed(3)}cqw`;

// Läge values (see mode mapping in pages/inside/controller.tsx) where the
// 3-way valve routes the heating circuit to the hot water tank.
const HOT_WATER_MODES = [3, 7]; // Varmvatten, Anti legionella

// A radiator return above this is not physically plausible and points to a
// broken or disconnected sensor.
const RETURN_SENSOR_FAULT_ABOVE = 90;

// Plausible [min, max] per metric. A reading outside the range almost always
// means a broken or disconnected sensor (typical fault readings are -40, 200,
// 850) rather than a real value.
const SENSOR_LIMITS: Record<string, [number, number]> = {
	outdoor: [-45, 45],
	indoor: [0, 40],
	indoorMin: [0, 40],
	indoorMax: [0, 40],
	indoorSetpoint: [5, 35],
	forwardSetpoint: [0, 80],
	heatCarrierForward: [0, 90],
	heatCarrierReturn: [0, 90],
	radiatorForward: [0, 90],
	radiatorReturn: [0, RETURN_SENSOR_FAULT_ABOVE],
	brineIn: [-25, 30],
	brineOut: [-25, 30],
	hotGasCompressor: [-20, 140],
	suctionGasTemperature: [-40, 40],
	superHeatTemperature: [-10, 40],
	lowPressureSidePressure: [0, 15],
	highPressureSidePressure: [0, 45],
	warmWater: [0, 95],
	twcSupplyTemp: [0, 95],
	twcReturnTemp: [0, 95],
	twcEndTankTemp: [0, 95],
	compressor: [0, 100],
	pumpBrine: [0, 100],
	pumpHeat: [0, 100],
	pumpRadiator: [0, 100],
};

// Thermia Genesis reports exactly 200 for the system supply/return line
// sensors when they are not installed (see pkg/controller/thermiagenesis).
const UNCONNECTED_SENSORS: Record<string, { keys: string[]; value: number }> = {
	thermiagenesis: { keys: ['radiatorForward', 'radiatorReturn'], value: 200 },
};

// Drops readings that mean "no sensor installed" for this heat pump type, so
// they show as missing rather than as a sensor fault.
export const withoutUnconnectedSensors = (metrics: Record<string, any>, heatControlType?: string) => {
	const rule = heatControlType ? UNCONNECTED_SENSORS[heatControlType] : undefined;
	if (!rule) {
		return metrics;
	}
	return Object.fromEntries(Object.entries(metrics).filter(([key, v]) => !(rule.keys.includes(key) && v === rule.value)));
};

export const isSensorFault = (key: string, v: unknown) => {
	const n = num(v);
	const limits = SENSOR_LIMITS[key];
	return n !== undefined && limits !== undefined && (n < limits[0] || n > limits[1]);
};

export const PIPE = {
	brineIn: 'oklch(0.62 0.12 235)',
	brineOut: 'oklch(0.74 0.08 225)',
	suction: 'oklch(0.68 0.10 250)',
	hotGas: 'oklch(0.58 0.18 25)',
	liquid: 'oklch(0.62 0.12 310)',
	supply: 'oklch(0.64 0.16 40)',
	return: 'oklch(0.74 0.10 55)',
	fault: 'oklch(0.52 0.18 28)',
};

const flow = keyframes`to { stroke-dashoffset: -28; }`;

const num = (v: unknown): number | undefined => (typeof v === 'number' && isFinite(v) ? v : undefined);

const fmt = (v: unknown, suffix = '') => {
	const n = num(v);
	if (n === undefined) {
		return '–';
	}
	return Math.round(n * 100) / 100 + suffix;
};

interface Palette {
	label: string;
	value: string;
	heading: string;
	strong: string;
	surface: string;
	cabinetStroke: string;
	track: string;
	outline: string;
	collectorFill: string;
	collectorStroke: string;
	evaporatorFill: string;
	condenserFill: string;
	roofFill: string;
	tankFill: string;
	mode: string;
}

interface LabelProps {
	x: number;
	y: number;
	size?: number;
	anchor?: 'start' | 'middle' | 'end';
	color?: string;
	bold?: boolean;
	caps?: boolean;
	children: ReactNode;
}

const Label = ({ x, y, size = 13, anchor = 'start', color, bold, caps, children }: LabelProps) => {
	const tx = { start: '0', middle: '-50%', end: '-100%' }[anchor];
	return (
		<Box
			position='absolute'
			left={`${(x * 100 / W).toFixed(3)}%`}
			top={`${(y * 100 / H).toFixed(3)}%`}
			transform={`translate(${tx},-80%)`}
			whiteSpace='nowrap'
			lineHeight={1}
			fontSize={px(size)}
			fontWeight={bold ? 700 : undefined}
			letterSpacing={caps ? px(1.5) : undefined}
			color={color}
		>
			{children}
		</Box>
	);
};

// White dashed overlay that makes a pipe look like it is flowing.
const Flow = ({ d, animation, width = 2.5 }: { d: string; animation: string; width?: number }) =>
	<chakra.path d={d} stroke='#fff' strokeWidth={width} strokeDasharray='4 10' fill='none' animation={animation} />;

interface HeatpumpDiagramProps {
	metrics: Record<string, any>;
	modeLabel: string;
}

export const HeatpumpDiagram = ({ metrics: m, modeLabel }: HeatpumpDiagramProps) => {
	const reducedMotion = usePrefersReducedMotion();
	const c: Palette = {
		label: useColorModeValue('#6b7280', '#a0aec0'),
		value: useColorModeValue('#1f2937', '#f7fafc'),
		heading: useColorModeValue('#9ca3af', '#718096'),
		strong: useColorModeValue('#374151', '#e2e8f0'),
		surface: useColorModeValue('#ffffff', '#1a202c'),
		cabinetStroke: useColorModeValue('#cbd5e1', '#4a5568'),
		track: useColorModeValue('#e5e7eb', '#4a5568'),
		outline: useColorModeValue('#9ca3af', '#718096'),
		collectorFill: useColorModeValue('oklch(0.96 0.02 235)', 'oklch(0.30 0.04 235)'),
		collectorStroke: useColorModeValue('oklch(0.80 0.06 235)', 'oklch(0.45 0.06 235)'),
		evaporatorFill: useColorModeValue('oklch(0.93 0.04 235)', 'oklch(0.32 0.05 235)'),
		condenserFill: useColorModeValue('oklch(0.94 0.04 40)', 'oklch(0.33 0.05 40)'),
		roofFill: useColorModeValue('oklch(0.95 0.02 40)', 'oklch(0.32 0.03 40)'),
		tankFill: useColorModeValue('oklch(0.96 0.03 40)', 'oklch(0.30 0.04 40)'),
		mode: useColorModeValue('oklch(0.50 0.14 40)', 'oklch(0.75 0.13 45)'),
	};

	const hwActive = HOT_WATER_MODES.includes(num(m.mode) ?? -1);
	// Speed in percent for a pump/compressor, 0 when the reading is a sensor fault
	// so a broken sensor does not show as flow at full speed.
	const pct = (key: string) => isSensorFault(key, m[key]) ? 0 : num(m[key]) ?? 0;
	const anim = (key: string) => {
		const p = pct(key);
		if (reducedMotion || p <= 0) {
			return 'none';
		}
		return `${flow} ${(0.8 * 100 / p).toFixed(2)}s linear infinite`;
	};
	const brine = anim('pumpBrine');
	const refrigerant = anim('compressor');
	const heat = anim('pumpHeat');
	const house = hwActive ? 'none' : heat;
	const tank = hwActive ? heat : 'none';

	const C = 2 * Math.PI * 34;
	const compPct = pct('compressor');
	const returnFault = isSensorFault('radiatorReturn', m.radiatorReturn);
	// Many installations have no TWC, skip the block instead of showing empty rows.
	const hasTwc = ['twcSupplyTemp', 'twcReturnTemp', 'twcEndTankTemp'].some((key) => m[key] !== undefined);

	// Metric value in bold, flagged when the reading is outside its plausible range.
	const val = (key: string, suffix = '') => isSensorFault(key, m[key])
		? <b data-fault title='Kontrollera givare'>⚠ {fmt(m[key], suffix)}</b>
		: <b>{fmt(m[key], suffix)}</b>;

	return (
		<Box position='relative' minW='1150px' sx={{ containerType: 'inline-size' }}>
			<svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
				{/* ground loop and heat pump cabinet */}
				<rect x='30' y='150' width='140' height='260' rx='10' fill={c.collectorFill} stroke={c.collectorStroke} />
				<rect x='350' y='100' width='510' height='380' rx='14' fill={c.surface} stroke={c.cabinetStroke} strokeDasharray='6 5' />

				{/* pipes */}
				<path d='M170 200 H380' stroke={PIPE.brineIn} strokeWidth='8' fill='none' strokeLinecap='round' />
				<path d='M380 380 H170' stroke={PIPE.brineOut} strokeWidth='8' fill='none' strokeLinecap='round' />
				<path d='M430 190 H571' stroke={PIPE.suction} strokeWidth='6' fill='none' />
				<path d='M639 190 H780' stroke={PIPE.hotGas} strokeWidth='6' fill='none' />
				<path d='M780 390 H430' stroke={PIPE.liquid} strokeWidth='6' fill='none' />
				<path d='M830 200 H1180' stroke={PIPE.supply} strokeWidth='8' fill='none' strokeLinecap='round' />
				<path d='M940 200 V300' stroke={PIPE.supply} strokeWidth='8' fill='none' opacity={hwActive ? 1 : 0.35} />
				<path d='M1200 280 V520 H880 V380 H830' stroke={PIPE.return} strokeWidth='8' fill='none' strokeLinejoin='round' />
				<path d='M970 480 V520' stroke={PIPE.return} strokeWidth='8' fill='none' opacity={hwActive ? 1 : 0.35} />

				{/* flow overlays, speed follows the pump driving each circuit */}
				<Flow d='M170 200 H380' animation={brine} />
				<Flow d='M380 380 H170' animation={brine} />
				<Flow d='M430 190 H571' animation={refrigerant} width={2} />
				<Flow d='M639 190 H780' animation={refrigerant} width={2} />
				<Flow d='M780 390 H430' animation={refrigerant} width={2} />
				<Flow d='M830 200 H940' animation={heat} />
				<Flow d='M940 200 H1180' animation={house} />
				<Flow d='M940 200 V300' animation={tank} />
				<Flow d='M1200 280 V520 H970' animation={house} />
				<Flow d='M970 480 V520' animation={tank} />
				<Flow d='M970 520 H880 V380 H830' animation={heat} />

				{/* brine pump */}
				<circle cx='280' cy='200' r='16' fill={c.surface} stroke={PIPE.brineIn} strokeWidth='3' />
				<polygon points='274,191 274,209 289,200' fill={PIPE.brineIn} />

				{/* evaporator / condenser */}
				<rect x='380' y='170' width='50' height='240' rx='6' fill={c.evaporatorFill} stroke={PIPE.brineIn} strokeWidth='2' />
				<rect x='780' y='170' width='50' height='240' rx='6' fill={c.condenserFill} stroke={PIPE.supply} strokeWidth='2' />

				{/* compressor, ring shows speed */}
				<circle cx='605' cy='190' r='34' fill={c.surface} stroke={c.track} strokeWidth='6' />
				{compPct > 0 &&
					<circle cx='605' cy='190' r='34' fill='none' stroke={PIPE.hotGas} strokeWidth='6'
						strokeDasharray={`${(C * compPct / 100).toFixed(1)} ${C.toFixed(1)}`}
						transform='rotate(-90 605 190)' strokeLinecap='round' />}

				{/* expansion valve and 3-way valve */}
				<polygon points='605,374 621,390 605,406 589,390' fill={c.surface} stroke={PIPE.liquid} strokeWidth='3' />
				<polygon points='940,186 954,200 940,214 926,200' fill={c.surface} stroke={c.label} strokeWidth='2.5' />

				{/* house */}
				<polygon points='1170,110 1280,55 1390,110' fill={c.roofFill} stroke={c.outline} strokeWidth='2' strokeLinejoin='round' />
				<rect x='1180' y='110' width='200' height='170' fill={c.surface} stroke={c.outline} strokeWidth='2' />

				{/* hot water tank */}
				<rect x='895' y='300' width='90' height='180' rx='14' fill={c.tankFill} stroke={PIPE.supply} strokeWidth='2' opacity={hwActive ? 1 : 0.6} />

				{/* heating carrier pump, on the shared return so it drives both house and tank */}
				<circle cx='925' cy='520' r='16' fill={c.surface} stroke={PIPE.supply} strokeWidth='3' />
				<polygon points='931,511 931,529 916,520' fill={PIPE.supply} />
			</svg>

			<Box color={c.label} sx={{ '& b': { fontWeight: 700, color: c.value }, '& b[data-fault]': { color: PIPE.fault } }}>
				{/* ground loop */}
				<Label x={100} y={275} size={14} anchor='middle' bold color={c.strong}>Köldbärar-</Label>
				<Label x={100} y={293} size={14} anchor='middle' bold color={c.strong}>krets</Label>
				<Label x={190} y={170}>Köldbärare in {val('brineIn', '°')}</Label>
				<Label x={280} y={238} anchor='middle'>Köldbärarpump {val('pumpBrine', '%')}</Label>
				<Label x={190} y={408}>Köldbärare ut {val('brineOut', '°')}</Label>

				{/* refrigerant cycle */}
				<Label x={368} y={126} bold caps>VÄRMEPUMP</Label>
				<Label x={405} y={432} size={12} anchor='middle'>Förångare</Label>
				<Label x={805} y={432} size={12} anchor='middle'>Kondensor</Label>
				<Label x={440} y={176}>Suggas {val('suctionGasTemperature', '°')}</Label>
				<Label x={440} y={214} size={12}>Lågtryck {val('lowPressureSidePressure')} bar(g)</Label>
				<Label x={440} y={231} size={12}>Överhettning {val('superHeatTemperature')} K</Label>
				<Label x={652} y={176}>Hetgas {val('hotGasCompressor', '°')}</Label>
				<Label x={652} y={214} size={12}>Högtryck {val('highPressureSidePressure')} bar(g)</Label>
				<Label x={605} y={196} size={17} anchor='middle'>{val('compressor', '%')}</Label>
				<Label x={605} y={248} size={12} anchor='middle'>Kompressor</Label>
				<Label x={605} y={428} size={12} anchor='middle'>Expansionsventil</Label>
				<Label x={605} y={300} size={11} anchor='middle' caps color={c.heading}>LÄGE</Label>
				<Label x={605} y={328} size={24} anchor='middle' bold color={c.mode}>{modeLabel}</Label>

				{/* heating circuit */}
				<Label x={845} y={160}>Värmebärare</Label>
				<Label x={845} y={178} size={14}>{val('heatCarrierForward', '°')}</Label>
				<Label x={975} y={184}>Framledning {val('radiatorForward', '°')}</Label>
				<Label x={975} y={226} size={12}>Börvärde {val('forwardSetpoint', '°')}</Label>
				<Label x={852} y={470} anchor='end'>Värmebärare retur {val('heatCarrierReturn', '°')}</Label>
				<Label x={925} y={556} anchor='middle'>Värmebärarpump {val('pumpHeat', '%')}</Label>

				{/* house */}
				<Label x={1395} y={30} anchor='end'>Utomhus <chakra.span fontSize={px(15)}>{val('outdoor', '°')}</chakra.span></Label>
				<Label x={1198} y={138} size={11} caps color={c.heading}>INOMHUS</Label>
				<Label x={1198} y={176} size={30}>{val('indoor', '°')}</Label>
				<Label x={1198} y={196} size={12}>medel</Label>
				<Label x={1198} y={228}>Min</Label>
				<Label x={1362} y={228} anchor='end'>{val('indoorMin', '°')}</Label>
				<Label x={1198} y={254}>Börvärde</Label>
				<Label x={1362} y={254} anchor='end'>{val('indoorSetpoint', '°')}</Label>
				<Label x={1215} y={330}>Framledning retur</Label>
				<Label x={1215} y={350} size={14}>{val('radiatorReturn', '°')}</Label>
				{returnFault && <Label x={1215} y={370} size={12} color={PIPE.fault}>⚠ Kontrollera givare</Label>}

				{/* hot water tank */}
				<Label x={940} y={336} size={11} anchor='middle'>Varmvatten</Label>
				<Label x={940} y={362} size={18} anchor='middle'>{val('warmWater', '°')}</Label>
				{hasTwc && <>
					<Label x={1005} y={318} size={11} caps color={c.heading}>TWC</Label>
					<Label x={1005} y={345}>Framledning</Label>
					<Label x={1180} y={345} anchor='end'>{val('twcSupplyTemp', '°')}</Label>
					<Label x={1005} y={370}>Retur</Label>
					<Label x={1180} y={370} anchor='end'>{val('twcReturnTemp', '°')}</Label>
					<Label x={1005} y={395}>Slutberedare</Label>
					<Label x={1180} y={395} anchor='end'>{val('twcEndTankTemp', '°')}</Label>
				</>}
			</Box>
		</Box>
	);
};

const LEGEND = [
	{ label: 'Köldbärare', color: PIPE.brineIn },
	{ label: 'Köldmedium, hetgas', color: PIPE.hotGas },
	{ label: 'Köldmedium, vätska', color: PIPE.liquid },
	{ label: 'Värmebärare', color: PIPE.supply },
];

export const HeatpumpDiagramLegend = () => (
	<Flex gap='18px' wrap='wrap' fontSize='12px' color={useColorModeValue('#6b7280', 'gray.400')} px='8px' pt='6px' pb='4px'>
		{LEGEND.map(({ label, color }) => (
			<Flex key={label} align='center' gap='6px'>
				<Box w='18px' h='6px' borderRadius='3px' bg={color} />
				{label}
			</Flex>
		))}
		<Box ml='auto'>Flödeshastighet följer pumpvarvtal</Box>
	</Flex>
);
